import type { GetProductSellingPlansQuery } from "@/types/storefront-api.gen";

type ProductVariantNode = Extract<
  GetProductSellingPlansQuery["nodes"][number],
  { __typename?: "ProductVariant" }
>;

type SellingPlan =
  ProductVariantNode["sellingPlanAllocations"]["edges"][number]["node"]["sellingPlan"];

const isProductVariant = (
  node: GetProductSellingPlansQuery["nodes"][number],
): node is ProductVariantNode => {
  return node?.__typename === "ProductVariant";
};

const isMonthly = (plan: Pick<SellingPlan, "deliveryPolicy">) => {
  return plan.deliveryPolicy?.interval === "MONTH" && plan.deliveryPolicy?.intervalCount === 1;
};

const getSuspectVariantIds = (
  nodes: GetProductSellingPlansQuery["nodes"],
  requestedVariantIds: string[],
) => {
  return requestedVariantIds.filter((_, index) => {
    const node = nodes[index];

    return (
      !isProductVariant(node) ||
      !node.sellingPlanAllocations.edges.some(({ node: allocation }) => {
        return isMonthly(allocation.sellingPlan);
      })
    );
  });
};

export const createCareplanSellingPlansDataModel = (
  sellingPlansQuery?: GetProductSellingPlansQuery,
  requestedVariantIds: string[] = [],
) => {
  if (!sellingPlansQuery) {
    return null;
  }

  const sellingPlans = sellingPlansQuery.nodes.filter(isProductVariant).flatMap((node) => {
    return node.sellingPlanAllocations.edges.map(({ node: allocationNode }) => {
      const { priceAdjustments, sellingPlan } = allocationNode;

      return {
        ...sellingPlan,
        ...priceAdjustments?.[0],
      };
    });
  });

  const uniqueSellingPlans = sellingPlans.filter((plan, index, self) => {
    return (
      index ===
      self.findIndex((sellingPlan) => {
        return sellingPlan.id === plan.id;
      })
    );
  });

  return {
    all: uniqueSellingPlans,
    monthly: uniqueSellingPlans.find(isMonthly),
    suspectVariantIds: getSuspectVariantIds(sellingPlansQuery.nodes, requestedVariantIds),
  };
};
